import React from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import type { PipMode, PipTelemetry } from "@client/types/pip";
import { formatMetric } from "@client/utils/formatMetric";
import { Card, CardContent } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: PipTelemetry | undefined;
}

interface NodeProps {
  label: string;
  primary: string;
  secondary?: string;
  emoji: string;
  active: boolean;
  className?: string;
}

const Node: React.FC<NodeProps> = ({ label, primary, secondary, emoji, active, className }) => (
  <div
    className={`flex flex-col items-center justify-center gap-1 rounded-2xl border-2 px-4 py-3 min-w-32 transition-all ${
      active ? "border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20" : "border-border bg-muted/30 opacity-70"
    } ${className ?? ""}`}
  >
    <div className="text-2xl">{emoji}</div>
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="text-xl font-bold tabular-nums">{primary}</div>
    {secondary && <div className="text-xs tabular-nums text-muted-foreground">{secondary}</div>}
  </div>
);

interface ArrowProps {
  direction: "right" | "left" | "down" | "up";
  active: boolean;
  label?: string;
  /** Na mobilu (pod `sm`) otočí vodorovný glyf o 90° → svislý tok ve stacku. */
  stackRotate?: boolean;
}

const Arrow: React.FC<ArrowProps> = ({ direction, active, label, stackRotate }) => {
  const arrow = direction === "right" ? "→" : direction === "left" ? "←" : direction === "down" ? "↓" : "↑";
  const animClass = active ? "animate-pulse" : "opacity-30";
  const colorClass = active ? "text-green-500" : "text-muted-foreground";
  return (
    <div className={`flex flex-col items-center justify-center gap-1 ${animClass}`}>
      <span className={`text-3xl ${colorClass} ${stackRotate ? "rotate-90 sm:rotate-0" : ""}`}>{arrow}</span>
      {label && <span className="text-xs text-muted-foreground tabular-nums">{label}</span>}
    </div>
  );
};

/**
 * Český label pro PIP režim. Drží se zde (ne v DB), protože jde o enum mapping
 * (raw "B" → "Baterie"), nikoli formátování hodnoty. `mode/val` v DB má STRING valueType
 * + nickname "Režim měniče" — to je pro label fieldu. Sám raw enum jde přes tuhle mapu.
 */
const modeLabel = (mode: PipMode | undefined): string => {
  switch (mode) {
    case "P":
      return "Power on";
    case "S":
      return "Standby";
    case "L":
      return "Síť";
    case "B":
      return "Baterie";
    case "F":
      return "Porucha";
    case "H":
      return "Eco";
    default:
      return "—";
  }
};

export const PipFlowDiagram: React.FC<Props> = ({ slug, telemetry }) => {
  const topic = (metric: string) => `zige/pip-${slug}/${metric}/val`;
  const pvWMeta = useMetricMeta(topic("pv-w"));
  const pvVMeta = useMetricMeta(topic("pv-v"));
  const pvAMeta = useMetricMeta(topic("pv-a"));
  const gridVMeta = useMetricMeta(topic("grid-v"));
  const gridHzMeta = useMetricMeta(topic("grid-hz"));
  const acOutWMeta = useMetricMeta(topic("ac-out-w"));
  const busVMeta = useMetricMeta(topic("bus-v"));
  const heatsinkMeta = useMetricMeta(topic("heatsink-temp"));
  const batteryChargeAMeta = useMetricMeta(topic("battery-charge-a"));
  const batteryDischargeAMeta = useMetricMeta(topic("battery-discharge-a"));
  const batterySocMeta = useMetricMeta(topic("battery-soc"));
  const batteryVMeta = useMetricMeta(topic("battery-v"));
  const loadPercentMeta = useMetricMeta(topic("ac-out-percent"));
  const loadVaMeta = useMetricMeta(topic("ac-out-va"));

  if (!telemetry) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">Čekám na první telemetrii…</CardContent>
      </Card>
    );
  }

  const pvActive = telemetry.pvChargingPowerW > 0;
  const gridActive = telemetry.mode === "L" && telemetry.gridVoltageV > 100;
  const batteryCharging = telemetry.batteryChargingA > 0;
  const batteryDischarging = telemetry.batteryDischargeA > 0;
  const loadActive = telemetry.acOutputActiveW > 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col items-center gap-2 md:gap-4">
          {/* Top row: PV */}
          <Node
            label="PV (Solar)"
            primary={formatMetric(telemetry.pvChargingPowerW, pvWMeta)}
            secondary={`${formatMetric(telemetry.pvInputVoltageV, pvVMeta)} / ${formatMetric(telemetry.pvInputCurrentA, pvAMeta)}`}
            emoji="☀️"
            active={pvActive}
          />
          <Arrow
            direction="down"
            active={pvActive}
            label={pvActive ? formatMetric(telemetry.pvChargingPowerW, pvWMeta) : undefined}
          />

          {/* Middle row: Grid ↔ Inverter ↔ Battery — na mobilu sloupec, od sm řádek */}
          <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-4 w-full justify-center">
            <Node
              label="Síť (Grid)"
              primary={formatMetric(telemetry.gridVoltageV, gridVMeta)}
              secondary={formatMetric(telemetry.gridFrequencyHz, gridHzMeta)}
              emoji="⚡"
              active={gridActive}
            />
            <Arrow direction="right" active={gridActive} stackRotate />
            <Node
              label={`Měnič — ${modeLabel(telemetry.mode)}`}
              primary={formatMetric(telemetry.acOutputActiveW, acOutWMeta)}
              secondary={`Bus ${formatMetric(telemetry.busVoltageV, busVMeta)} · ${formatMetric(telemetry.heatsinkTempC, heatsinkMeta)}`}
              emoji="🔌"
              active={true}
              className="border-blue-500 bg-blue-500/10"
            />
            <Arrow
              direction={batteryCharging ? "right" : "left"}
              active={batteryCharging || batteryDischarging}
              stackRotate
              label={
                batteryCharging
                  ? `+${formatMetric(telemetry.batteryChargingA, batteryChargeAMeta)}`
                  : batteryDischarging
                    ? `-${formatMetric(telemetry.batteryDischargeA, batteryDischargeAMeta)}`
                    : undefined
              }
            />
            <Node
              label="Baterie"
              primary={formatMetric(telemetry.batterySocPercent, batterySocMeta)}
              secondary={formatMetric(telemetry.batteryVoltageV, batteryVMeta)}
              emoji="🔋"
              active={batteryCharging || batteryDischarging}
            />
          </div>

          <Arrow
            direction="down"
            active={loadActive}
            label={loadActive ? formatMetric(telemetry.acOutputActiveW, acOutWMeta) : undefined}
          />

          {/* Bottom row: Load */}
          <Node
            label="Zátěž (Load)"
            primary={formatMetric(telemetry.acOutputActiveW, acOutWMeta)}
            secondary={`${formatMetric(telemetry.outputLoadPercent, loadPercentMeta)} · ${formatMetric(telemetry.acOutputApparentVA, loadVaMeta)}`}
            emoji="💡"
            active={loadActive}
          />
        </div>
      </CardContent>
    </Card>
  );
};
